import React, { useEffect, useState } from 'react';
import { getScratches } from '../../api/campaign';
import { Dropdown, DropdownItem, Table, TableBody, TableCell, TableHead, TableHeadCell, TableRow, Modal, ModalBody, ModalHeader, Spinner, Button } from "flowbite-react";
import img from '../../scratch_foreground.jpg';

export default function Data() {
    const [details, setDetails] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [imageUrl, setImageUrl] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [brandFilter, setBrandFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    useEffect(() => {
        applyFilters();
    }, [details, selectedDate, brandFilter, statusFilter]);

    const applyFilters = () => {
        let filtered = [...details];

        // Date filter - only apply if date is selected
        if (selectedDate) {
            filtered = filtered.filter(item => {
                const itemDate = new Date(item.dt).toISOString().split('T')[0];
                return itemDate === selectedDate;
            });
        }

        // Brand filter - only apply if brand is selected
        if (brandFilter === 'inv') {
            filtered = filtered.filter(item => item.innovate?.toLowerCase().includes('x'));
        } else if (brandFilter === 'atlas') {
            filtered = filtered.filter(item => item.atlas?.toLowerCase().includes('x'));
        } else if (brandFilter === 'all') {
            filtered = filtered.filter(item =>
                item.innovate?.toLowerCase().includes('x') ||
                item.atlas?.toLowerCase().includes('x')
            );
        }

        // Status filter - only apply if status is selected
        if (statusFilter === 'win') {
            filtered = filtered.filter(item => item.s === 1);
        } else if (statusFilter === 'lost') {
            filtered = filtered.filter(item => item.s === 0);
        }

        // Sort by ID in ascending order (1 to ...)
        filtered.sort((a, b) => a.id - b.id);

        setFilteredData(filtered);
    };

    const downloadCSV = () => {
        // Define CSV headers
        const headers = ['ID', 'Name', 'TP', 'Shop Name', 'Shop Territory', 'S', 'R', 'A', 'Date & Time', 'Atlas', 'Innovate', 'Bill Image'];
        
        // Convert data to CSV format
        const csvData = filteredData.map(item => [
            item.id,
            item.name,
            item.tp,
            item.shopName,
            item.shopTerritory || '',
            item.s,
            item.r,
            item.a,
            new Date(item.dt).toLocaleString(),
            item.atlas || '',
            item.innovate || '',
            item.billImg
        ]);

        // Combine headers and data
        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', `data_export_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // bill_img is now a direct URL in the file store, so there is nothing to fetch
    const getImage = (billImg) => setImageUrl(billImg || '');

    useEffect(() => {
        getDetails();
    }, []);

    const getDetails = async () => {
        setIsLoading(true);
        setLoadError('');
        try {
            setDetails(await getScratches());
        } catch (err) {
            console.error("error fetching data: ", err);
            setLoadError('Could not load data. Tap retry.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };

    const handleBrandFilter = (val) => {
        setBrandFilter(val);
    };

    const handleStatusFilter = (val) => {
        setStatusFilter(val);
    };

    const clearFilters = () => {
        setSelectedDate('');
        setBrandFilter('');
        setStatusFilter('');
    };

    return (
        <>
            <div className='flex flex-col text-center'>
                <h1 className='py-4 text-3xl font-bold'>Complete DashBoard</h1>

                <div className='px-8 py-4'>
                    <div className='flex gap-4 items-end'>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                                Filter by Date
                            </label>
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => handleDateChange(e.target.value)}
                                className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <Dropdown label="Select Brand">
                            <DropdownItem onClick={() => handleBrandFilter('')}>All Brands</DropdownItem>
                            {/* 'all' (Atlas + Innovate) is redundant while only Atlas runs.
                            <DropdownItem onClick={() => handleBrandFilter('all')}>All (Atlas + Innovate)</DropdownItem>
                            */}
                            {/* CAMPAIGN 2 IS ATLAS ONLY -- Innovate filter switched off.
                            <DropdownItem onClick={() => handleBrandFilter('inv')}>Innovate</DropdownItem>
                            */}
                            <DropdownItem onClick={() => handleBrandFilter('atlas')}>Atlas</DropdownItem>
                        </Dropdown>
                        <Dropdown label="Select Status">
                            <DropdownItem onClick={() => handleStatusFilter('')}>All Status</DropdownItem>
                            <DropdownItem onClick={() => handleStatusFilter('win')}>Win</DropdownItem>
                            <DropdownItem onClick={() => handleStatusFilter('lost')}>Lost</DropdownItem>
                        </Dropdown>
                        {(selectedDate || brandFilter || statusFilter) && (
                            <Button onClick={clearFilters} color="gray">
                                Clear Filters
                            </Button>
                        )}
                        <Button className='ml-auto' onClick={downloadCSV} color="green">
                            Download CSV
                        </Button>
                    </div>
                </div>

                <div className="mb-4 px-8 text-sm text-gray-600 text-left">
                    Showing {filteredData.length} of {details.length} records
                </div>

                {isLoading ? (
                    <div className="py-16 text-center">
                        <Spinner size="xl" />
                        <p className="mt-3 text-sm text-gray-500">Loading data&hellip;</p>
                    </div>
                ) : loadError ? (
                    <div className="py-16 text-center">
                        <p className="mb-3 text-red-600">{loadError}</p>
                        <Button onClick={getDetails}>Retry</Button>
                    </div>
                ) : (
                <div className="overflow-x-auto px-8">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeadCell>ID</TableHeadCell>
                                <TableHeadCell>Name</TableHeadCell>
                                <TableHeadCell>TP</TableHeadCell>
                                <TableHeadCell>Shop Name</TableHeadCell>
                                <TableHeadCell>Shop Territory</TableHeadCell>
                                <TableHeadCell>S</TableHeadCell>
                                <TableHeadCell>R</TableHeadCell>
                                <TableHeadCell>A</TableHeadCell>
                                <TableHeadCell>Date & Time</TableHeadCell>
                                <TableHeadCell>Atlas</TableHeadCell>
                                <TableHeadCell>Innovate</TableHeadCell>
                                <TableHeadCell><span className="sr-only">Bill</span></TableHeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="divide-y">
                            {filteredData.map((data, index) => (
                                <TableRow key={index} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                                    <TableCell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                                        {data.id}
                                    </TableCell>
                                    <TableCell>{data.name}</TableCell>
                                    <TableCell>{data.tp}</TableCell>
                                    <TableCell>{data.shopName}</TableCell>
                                    <TableCell>{data.shopTerritory || '-'}</TableCell>
                                    <TableCell>{data.s}</TableCell>
                                    <TableCell>{data.r}</TableCell>
                                    <TableCell>{data.a}</TableCell>
                                    <TableCell>{new Date(data.dt).toLocaleString()}</TableCell>
                                    <TableCell>{data.atlas || '-'}</TableCell>
                                    <TableCell>{data.innovate || '-'}</TableCell>
                                    <TableCell>
                                        <a href="#" onClick={() => { setOpenModal(true); getImage(data.bill_img); }} className="font-medium text-primary-600 hover:underline dark:text-primary-500">
                                            View Bill
                                        </a>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                )}
            </div>
            <>
                <Modal show={openModal} onClose={() => { setOpenModal(false); setImageUrl(null); }}>
                    <ModalHeader>Bill Image</ModalHeader>
                    <ModalBody>
                        {imageUrl ?
                            <div className='flex justify-center items-center'>
                                <img src={imageUrl} alt="Bill" className="max-h-[70vh] w-auto object-contain" />
                            </div>
                            :
                            <>
                                <div className="text-center">
                                    <Spinner aria-label="Center-aligned Large spinner example" />
                                </div>
                            </>}
                    </ModalBody>
                </Modal>
            </>
        </>
    );
}